import {  useCallback, useState } from "react";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { processError } from "../../../../common/utils/base_error";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useCreateDebounceHandler } from "../../../../common/hooks/use_debounced_callback";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMedicaoFilters } from "../hooks/medicao_terceirizacao/filter";
import { useMedicaoPagination } from "../hooks/medicao_terceirizacao/pagination";
import { AvaliacaoFornecedorLista } from "../../domain/models/avaliacao_fornecedor_lista";
import { AvaliacaoFornecedorForm, AvaliacaoFornecedorSchema } from "../schemas/Contrato/avaliacao_fornecedor";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";



export const useAvaliacaoFornecimentoCadastroViewModel = () => {
  const {
    commons: {  getAllLogAlteracaoUseCase, getAllLogAlteracaoByParentUseCase},
    terceirizacao: {getAllListagemMedicaoEntregaUseCase, getAvaliacaoFornecedorUseCase, getAllListagemFormularioAvaliacaoUseCase, updateAvaliacaoUseCase},
  } = useDependencyInjection();
  const { id } = useParams();

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoByParentUseCase: getAllLogAlteracaoByParentUseCase,
    getAllLogAlteracaoUseCase: getAllLogAlteracaoUseCase,
    onSetError: (e) => pushErrorNotification("Erro", [e]),
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [openLegendaModal, setOpenLegendaModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [idMedicaoTerceirizacao, setIdMedicaoTerceirizacao] = useState<number>();
  const [AvaliacaoLista, setAvaliacaoLista] = useState<AvaliacaoFornecedorLista[]>([]);
  const [selectedAvaliacao, setSelectedAvaliacao] = useState<AvaliacaoFornecedorLista>();
  const {pushErrorNotification, pushInfoNotification, pushWarnNotification } = usePageLoader();
  const filters = useMedicaoFilters();
  const pagination = useMedicaoPagination();
  const navigate = useNavigate();
  const [notaMedia, setNotaMedia] = useState<number>(0);

  const hookForm = useForm<AvaliacaoFornecedorForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(AvaliacaoFornecedorSchema),
    defaultValues: {

    }
  });
  const [criterioItems] = hookForm.watch(['criterioItems']);

  
  // #region Funções de carregamento
  const loadPageData = useCallback(async (idOverride?: number) => {
    try {
      const idToLoad = idOverride 
        ?? (filters.IdMedicaoTerceirizacao && filters.IdMedicaoTerceirizacao > 0 ? filters.IdMedicaoTerceirizacao : undefined)
        ?? idMedicaoTerceirizacao;

      if (!idToLoad) return; 

      const [
        requestMedicao, 
        formularioData, 
        avaliacaoData   
      ] = await Promise.all([
        // Chamada 1: Info do Cabeçalho (Fornecedor/CentroCusto)
        getAllListagemMedicaoEntregaUseCase.getAllListagemMedicaoEntrega(
          { IdMedicaoTerceirizacao: idToLoad }, 1, 1
        ),
        // Chamada 2: Info do Formulário (Notas, Datas, Radios)
        getAllListagemFormularioAvaliacaoUseCase.getAllFormularioAvaliacao(Number(idToLoad)),
        // Chamada 3: Itens da Grid (Critérios)
        getAvaliacaoFornecedorUseCase.getAvaliacaoFornecedor(Number(idToLoad))
      ]);

      const medicaoInfo = getAllListagemMedicaoEntregaUseCase.items && getAllListagemMedicaoEntregaUseCase.items.length > 0 
        ? getAllListagemMedicaoEntregaUseCase.items[0] 
        : null;

      
      const formData = {
        // --- Dados da Medição (Cabeçalho) ---
        NomeCentroCusto: medicaoInfo?.NomeCentroCusto,
        NomeFornecedor: medicaoInfo?.NomeFornecedor,
        
        // --- Dados do Formulário Principal ---
        NrMedicaoTerceirizacao: formularioData?.NrMedicaoTerceirizacao ?? String(medicaoInfo?.NrMedicaoTerceirizacao ?? ""),
        
        Data: formularioData?.Data,
        Entregue: formularioData?.Entregue ?? 0, 
        NroNotaFiscal: formularioData?.NroNotaFiscal,
        NroNotaFiscalEntregue: formularioData?.NroNotaFiscalEntregue ?? 2, 
        Observacao: formularioData?.Observacao,
        IdFuncionarioAvaliador: formularioData?.IdFuncionarioAvaliador,

        // --- Dados da Grid (Critérios) ---
        // Mapeamos diretamente aqui para o reset já preencher tudo]
        criterioItems: avaliacaoData?.criterioItens.map(x => ({
          idCriterioAvaliacaoFornecedor: x.idCriterioAvaliacaoFornecedor,
          idItemAvaliacaoFornecedor: x.idItemAvaliacaoFornecedor,
          descricao: x.descricao,
          ordem: x.ordem,
          nota: x.nota,
        })) ?? []
      };
      if (avaliacaoData) {
          setNotaMedia(avaliacaoData.notaMedia ?? 0); 
      }

      hookForm.reset(formData);

    } catch (e: unknown) {
      console.error(e);
      throw new Error("Falha ao carregar os dados da página!");
    }
  }, [filters.IdMedicaoTerceirizacao, idMedicaoTerceirizacao, getAllListagemMedicaoEntregaUseCase, getAllListagemFormularioAvaliacaoUseCase, getAvaliacaoFornecedorUseCase, hookForm]);
  
  
  //#endregion

  // #region useEffect
  
  usePageStartBootstrap(async () => {
    try {
      if(id){
        setIdMedicaoTerceirizacao(Number(id));
        await loadPageData(Number(id));
      }
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados da pagina!')[0]);
    }
  });
  
  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      if(id){
        setIdMedicaoTerceirizacao(Number(id));
        await loadPageData( Number(id));
      }
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [id, loadPageData, pushErrorNotification]);
  //#endregion
    
  // #region Eventos

  const handleAvaliacaoSubmit = useCallback(async (form: AvaliacaoFornecedorForm) => {
      const temNotaBaixa = form.criterioItems.some(x => x.nota !== null && x.nota > 0 && x.nota < 3);
      
      if (temNotaBaixa) {
        if (!form.Observacao || form.Observacao.trim().length === 0) {
          pushWarnNotification('Atenção', 'Você deve preencher a observação, pois existem notas menores que 3!');
          return; 
        }
      }
      if (idMedicaoTerceirizacao) {
        try {
          setSaving(true);
  
          await updateAvaliacaoUseCase.updateAvaliacao(
            idMedicaoTerceirizacao,
            form.Entregue ?? 0,
            form.NroNotaFiscalEntregue ?? 2,
            form.Data,
            form.IdFuncionarioAvaliador ?? null,
            form.NroNotaFiscal ?? null,
            form.Observacao ?? null,
           form.criterioItems.map(x => ({
            idCriterioAvaliacaoFornecedor: x.idCriterioAvaliacaoFornecedor,
            idItemAvaliacaoFornecedor: x.idItemAvaliacaoFornecedor ?? null, 
            nota: x.nota ?? null,
            descricao: x.descricao ?? null 
          })),
          );
  
          await loadPageData();
  
          pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
        } catch (e: unknown) {
          pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!')[0]);
        } finally {
          setSaving(false);
        }
      }
    }, [idMedicaoTerceirizacao, loadPageData, pushErrorNotification, pushInfoNotification, updateAvaliacaoUseCase]);
  
    
  const handleAvaliacaoNotaChange = useCallback((idCriterioAvaliacaoFornecedor: number, nota: number | null) => {
        const criterioItems = hookForm.getValues('criterioItems');
        const itemIdx = criterioItems.findIndex(x => x.idCriterioAvaliacaoFornecedor === idCriterioAvaliacaoFornecedor);
        if (itemIdx >= 0) {
          criterioItems[itemIdx].nota = nota;
          hookForm.setValue('criterioItems', [...criterioItems]);
        }
      }, [hookForm]);
  
  
  const handleNavigateBack = useCallback(() => {
    navigate(`/MedicaoTerceirizacao_entrega/`)
    }, [navigate]);
  //#endregion

  const handleOpenChangeHistory = useCallback(
    (idTabela: number | null, tipoTabela: LogTipoTabela | null) => {
      if (idTabela && tipoTabela) {
        changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
      } else {
        if (idMedicaoTerceirizacao) {
          changeHistory.handleOpenChangeHistory(idMedicaoTerceirizacao, LogTipoTabela.AVALIACAO_FORNECEDOR, true);
        }
      }
    },
    [changeHistory, idMedicaoTerceirizacao],
  );

    
    
  return {
    loading,
    errorMessage,
    saving,
    pagination,
    filters,
    AvaliacaoLista,
    selectedAvaliacao,
    openLegendaModal,
    hookForm,
    criterioItems: criterioItems ?? [],
    notaMedia,
    changeHistory,
    idMedicaoTerceirizacao,
    handleReload,
    handleAvaliacaoNotaChange,
    handleAvaliacaoSubmit,
    handleNavigateBack,
    handleOpenChangeHistory,
  };
};
